import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Phone, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, RevealLines, Parallax } from "@/components/Reveal";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import SectionCTA from "@/components/SectionCTA";
import api, { API } from "@/lib/api";
import { SERVICES, IMG, VAN, BEFORE_AFTER, PROJECTS, REVIEWS, CONTACT } from "@/data/site";

const TEX = { water: "/textures/water_porcelain.jpg", veins: "/textures/stone_veins.jpg", grout: "/textures/grout_lines.jpg" };
const SWATCHES = [
  ["Calacatta Bianco", "/studio/tiles/tile_marble_white.jpg"], ["Travertino Romano", "/studio/tiles/tile_travertine.jpg"],
  ["Nero Marquina", "/studio/tiles/tile_black_marble.jpg"], ["Rovere Naturale", "/studio/tiles/tile_wood_oak.jpg"],
  ["Cemento Grigio", "/studio/tiles/tile_concrete.jpg"], ["Calacatta Oro", "/studio/tiles/tile_marble_grey.jpg"],
];

const HeroImage = ({ slides }) => {
  const [i, setI] = useState(0);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (reduce || slides.length < 2) return undefined;
    const id = setInterval(() => setI((n) => (n + 1) % slides.length), 6000);
    return () => clearInterval(id);
  }, [slides, reduce]);
  return (
    <div className="relative h-[56vh] lg:h-[78vh] overflow-hidden" data-testid="hero-carousel">
      <AnimatePresence>
        <motion.img key={slides[i]} src={slides[i]} alt="Rossi Ceramics project" className="absolute inset-0 w-full h-full object-cover"
          initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }} />
      </AnimatePresence>
      <div className="absolute bottom-0 left-0 bg-rossi-bg px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-rossi-stone">{String(i + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")} — Rossi project</div>
    </div>
  );
};

const Hero = ({ slides }) => (
  <section className="pt-32 lg:pt-40" data-testid="home-hero">
    <div className="container-rossi grid lg:grid-cols-12 gap-10 lg:gap-0">
      <div className="lg:col-span-5 lg:pr-12 flex flex-col justify-between pb-6">
        <div>
          <p className="eyebrow mb-8">Est. Stockport &middot; Italian heritage</p>
          <RevealLines as="h1" className="font-serif text-5xl sm:text-6xl xl:text-7xl leading-[0.98] text-rossi-cream uppercase tracking-[0.04em]" lines={["Italian", "materials."]} />
          <RevealLines as="p" className="gold-text text-4xl sm:text-5xl xl:text-6xl mt-3 leading-[1.05]" lines={["British craftsmanship."]} delay={0.3} />
          <Reveal delay={0.5}><p className="mt-8 text-rossi-cream/70 max-w-md leading-relaxed">Bathrooms, wet rooms, kitchens and floors — set out to the millimetre in porcelain, marble and stone by Vincent Rossi and his team.</p></Reveal>
        </div>
        <Reveal delay={0.6}>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/studio" data-testid="hero-ai-studio" className="btn-gold">Design in The Studio <ArrowUpRight size={15} /></Link>
            <Link to="/quote" data-testid="hero-quote" className="btn-ghost-gold">Free quote</Link>
            <Link to="/booking" data-testid="hero-book" className="btn-ghost-gold">Book a visit</Link>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-7 -mx-5 sm:mx-0"><HeroImage slides={slides} /></div>
    </div>
    <div className="container-rossi grid grid-cols-2 md:grid-cols-4 border-t border-rossi-cream/15 mt-10">
      {[["60+", "years in the trade"], ["0.5 mm", "setting-out tolerance"], ["1", "team, start to finish"], ["Free", "no-obligation quotes"]].map(([a, b], i) => (
        <div key={b} className={`py-6 ${i ? "md:border-l border-rossi-cream/15 md:pl-6" : ""}`}><p className="font-serif text-2xl text-rossi-cream">{a}</p><p className="text-[11px] font-mono uppercase tracking-[0.2em] text-rossi-stone mt-1">{b}</p></div>
      ))}
    </div>
  </section>
);

const Swatches = () => (
  <section className="border-y border-rossi-cream/15" data-testid="home-swatches">
    <div className="grid grid-cols-3 md:grid-cols-6">
      {SWATCHES.map(([n, src], i) => (
        <Link to="/collections" key={n} className={`group relative aspect-[3/4] overflow-hidden ${i ? "border-l border-rossi-bg" : ""}`}>
          <img src={src} alt={n} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2000ms] group-hover:scale-110" />
          <span className="absolute bottom-0 inset-x-0 p-3 text-[10px] font-mono uppercase tracking-[0.2em] text-white bg-gradient-to-t from-black/60 to-transparent">{n}</span>
        </Link>
      ))}
    </div>
  </section>
);

const Van = () => (
  <section className="grid md:grid-cols-12 border-b border-rossi-cream/15" data-testid="van-banner">
    <div className="md:col-span-7 relative min-h-[300px] md:min-h-[520px]"><img src={VAN[1]} alt="Rossi Ceramics branded van — Wet Room Specialists" className="absolute inset-0 w-full h-full object-cover" /></div>
    <div className="md:col-span-5 p-8 sm:p-14 flex flex-col justify-end">
      <p className="eyebrow mb-5">On the road</p>
      <RevealLines className="font-serif text-3xl sm:text-4xl text-rossi-cream leading-tight" lines={["Local, trusted,", "across the North West."]} />
      <p className="mt-5 text-rossi-cream/70 leading-relaxed max-w-md">Bathroom installations, wet rooms and all related building work. {CONTACT.membership}. 24hr call-out &middot; free estimates.</p>
      <div className="mt-8 flex flex-wrap gap-3">
        <a href={CONTACT.phoneHref} className="btn-gold" data-testid="van-call"><Phone size={14} /> {CONTACT.phone}</a>
        <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="btn-ghost-gold">WhatsApp</a>
      </div>
      <p className="mt-6 text-xs text-rossi-stone flex items-center gap-1.5"><MapPin size={12} className="text-rossi-gold" /> {CONTACT.address}</p>
    </div>
  </section>
);

const ServicesIndex = () => (
  <section className="container-rossi py-28 grid lg:grid-cols-12 gap-10">
    <div className="lg:col-span-4 lg:sticky lg:top-40 h-fit">
      <p className="eyebrow mb-5">What we do</p>
      <RevealLines className="font-serif text-3xl sm:text-4xl text-rossi-cream leading-tight" lines={["From a single tile", "to a whole home."]} />
      <Link to="/services" className="inline-flex items-center gap-2 mt-8 text-[12px] uppercase tracking-[0.18em] text-rossi-gold hover:gap-3 transition-all">How we work <ArrowUpRight size={14} /></Link>
    </div>
    <div className="lg:col-span-7 lg:col-start-6 border-t border-rossi-cream/15">
      {SERVICES.map((s, i) => (
        <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group grid grid-cols-[40px_1fr_auto] gap-5 items-baseline py-6 border-b border-rossi-cream/15">
          <span className="font-mono text-xs text-rossi-stone">{String(i + 1).padStart(2, "0")}</span>
          <div><h3 className="font-serif text-xl sm:text-2xl text-rossi-cream group-hover:text-rossi-gold transition-colors duration-500">{s.title}</h3><p className="text-sm text-rossi-cream/60 mt-1">{s.short}</p></div>
          <ArrowUpRight size={18} className="text-rossi-stone group-hover:text-rossi-gold group-hover:rotate-45 transition-all duration-500" />
        </Link>
      ))}
    </div>
  </section>
);

const StudioFeature = () => (
  <section className="relative" data-testid="home-studio">
    <Parallax src={TEX.water} alt="Water on porcelain" className="h-[70vh] min-h-[520px]" strength={12} />
    <div className="absolute inset-0 container-rossi flex items-end pb-12">
      <div className="bg-rossi-bg max-w-xl p-8 sm:p-12">
        <p className="eyebrow mb-5">The Studio</p>
        <RevealLines className="font-serif text-3xl sm:text-4xl text-rossi-cream leading-tight" lines={["Draw your room.", "Lay every tile."]} />
        <p className="mt-5 text-rossi-cream/70 leading-relaxed">A 3D design studio: set your room to the millimetre, lay real tiles with true-scale grout, place baths, showers and niches — then simply tell it what to change.</p>
        <Link to="/studio" data-testid="home-ai-cta" className="btn-gold mt-8">Open The Studio <ArrowUpRight size={15} /></Link>
      </div>
    </div>
  </section>
);

const Projects = () => (
  <section className="container-rossi py-28">
    <div className="flex items-end justify-between border-b border-rossi-cream/15 pb-6 mb-12">
      <RevealLines className="font-serif text-3xl sm:text-4xl text-rossi-cream" lines={["Recent work"]} />
      <Link to="/projects" className="text-[12px] uppercase tracking-[0.18em] text-rossi-gold flex items-center gap-2">All projects <ArrowUpRight size={14} /></Link>
    </div>
    <div className="grid md:grid-cols-12 gap-8">
      {PROJECTS.slice(0, 2).map((p, i) => (
        <Reveal key={p.name} delay={i * 0.15} className={i ? "md:col-span-5 md:mt-40" : "md:col-span-7"}>
          <Parallax src={p.after} alt={p.name} className={i ? "h-[380px]" : "h-[540px]"} strength={6} />
          <div className="flex justify-between gap-4 pt-4 border-t border-rossi-cream/15 mt-4"><h3 className="font-serif text-lg text-rossi-cream">{p.name}</h3><span className="text-[11px] font-mono uppercase tracking-[0.18em] text-rossi-stone">{p.service} &middot; {p.area}</span></div>
        </Reveal>
      ))}
    </div>
    <div className="grid md:grid-cols-12 gap-8 mt-24 items-center">
      <div className="md:col-span-4"><p className="eyebrow mb-4">Before / after</p><p className="serif-italic text-3xl text-rossi-cream leading-snug">Drag the line. The same room, the Rossi way.</p></div>
      <div className="md:col-span-8"><BeforeAfterSlider before={BEFORE_AFTER.before} after={BEFORE_AFTER.after} height="h-[460px]" beforeAlt="Bathroom before renovation" afterAlt="Finished Rossi bathroom" /></div>
    </div>
  </section>
);

const Vincent = () => (
  <section className="grid lg:grid-cols-12 border-y border-rossi-cream/15">
    <Parallax src={TEX.veins} alt="Calacatta marble veins" className="lg:col-span-5 h-[360px] lg:h-auto lg:min-h-[560px]" strength={8} />
    <div className="lg:col-span-7 p-8 sm:p-16 flex flex-col justify-center">
      <p className="eyebrow mb-6">Vincent Rossi</p>
      <RevealLines as="blockquote" className="serif-italic text-3xl sm:text-5xl text-rossi-cream leading-[1.15]" lines={["“Every project deserves", "precision, creativity", "and craftsmanship.”"]} />
      <p className="mt-8 text-rossi-cream/70 leading-relaxed max-w-xl">Vincent leads every Rossi Ceramics project personally — detail-focused, reliable and passionate about the quiet things that make a room last: flat walls, true falls, cuts that land where the eye won’t.</p>
      <Link to="/about" className="inline-flex items-center gap-2 mt-8 text-[12px] uppercase tracking-[0.18em] text-rossi-gold">Our story <ArrowUpRight size={14} /></Link>
    </div>
  </section>
);

const Reviews = () => (
  <section className="container-rossi py-28">
    <p className="eyebrow mb-10">Kind words</p>
    <div className="grid md:grid-cols-3 border-t border-rossi-cream/15">
      {REVIEWS.map((r, i) => (
        <Reveal key={i} delay={i * 0.1} className={`py-8 ${i ? "md:border-l border-rossi-cream/15 md:pl-8" : ""} md:pr-8`}>
          <p className="serif-italic text-xl text-rossi-cream/90 leading-relaxed">“{r.text}”</p>
          <p className="mt-6 text-[11px] font-mono uppercase tracking-[0.2em] text-rossi-stone">{r.name} &middot; {r.area}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

export default function Home() {
  const [slides, setSlides] = useState(IMG.hero);
  useEffect(() => {
    api.get("/gallery").then((r) => {
      const imgs = (r.data || []).map((g) => `${API}/gallery/file/${g.id}`);
      if (imgs.length) setSlides(imgs.slice(0, 6));
    }).catch(() => {});
  }, []);
  return (
    <div data-testid="home-page">
      <Seo title="Luxury Tiling, Bathrooms & The Studio in Stockport" description="Rossi Ceramics — Italian materials, British craftsmanship. Premium tiling, bathroom & wet room installation across Stockport and the North West, with The Studio 3D designer." />
      <Hero slides={slides} />
      <Swatches />
      <ServicesIndex />
      <Van />
      <StudioFeature />
      <Projects />
      <Vincent />
      <Reviews />
      <Parallax src={TEX.grout} alt="Travertine floor with crisp grout lines" className="h-[40vh]" strength={10} />
      <SectionCTA />
    </div>
  );
}
